import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, X, CalendarClock, ShieldCheck, Trash2 } from 'lucide-react';

const FREQ_LABEL = {
  monthly: 'every month',
  semimonthly: 'twice a month',
  biweekly: 'every two weeks',
  weekly: 'every week',
};

export const PayrollGiving = ({ businessId, campaigns = [], primaryColor = '#0A58E5' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [campaignId, setCampaignId] = useState('');
  const [amount, setAmount] = useState(25);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/payroll/my-pledges');
      setData(res.data);
    } catch (err) {
      console.error('Failed to load payroll pledges:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const campaign = campaigns.find(c => c.campaign_id === campaignId);
    if (!campaign) { toast.error('Pick a cause to give to'); return; }
    setSaving(true);
    try {
      await api.post('/api/payroll/pledge', {
        campaign_id: campaign.campaign_id,
        charity_ein: campaign.charity_ein,
        charity_name: campaign.charity_name,
        amount_per_period: Number(amount),
      });
      toast.success('Payroll gift set up — it starts on your next pay run');
      setShowForm(false);
      setCampaignId('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set up the payroll gift');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (pledgeId) => {
    try {
      await api.post(`/api/payroll/pledge/${pledgeId}/cancel`);
      toast.success('Payroll gift stopped');
      await load();
    } catch (err) {
      toast.error('Could not stop that gift');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-gray-400" /></div>
    );
  }

  const freq = FREQ_LABEL[data?.frequency] || 'every pay run';

  return (
    <div className="space-y-6" data-testid="payroll-giving-section">
      <div className="rounded-2xl p-6 text-white" style={{ background: `linear-gradient(135deg, ${primaryColor}, #1e1b4b)` }}>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/70">
          <ShieldCheck className="w-4 h-4" /> Fee-free giving
        </div>
        <h3 className="text-2xl font-bold mt-2">Give straight from your pay</h3>
        <p className="text-sm text-white/80 mt-2 max-w-xl leading-relaxed">
          Your company withholds the amount you choose {freq} and sends it on by bank transfer through Every.org.
          No card fees are taken out, so <strong>100% of your gift reaches the charity</strong>.
        </p>
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div>
            <p className="text-2xl font-black" data-testid="payroll-per-period">${(data?.per_period_total || 0).toLocaleString()}</p>
            <p className="text-xs text-white/70">Per pay run</p>
          </div>
          <div>
            <p className="text-2xl font-black" data-testid="payroll-annual">${(data?.annual_total || 0).toLocaleString()}</p>
            <p className="text-xs text-white/70">A year</p>
          </div>
          <div>
            <p className="text-2xl font-black" data-testid="payroll-given">${(data?.given_to_date || 0).toLocaleString()}</p>
            <p className="text-xs text-white/70">Given so far</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">My payroll gifts</h4>
          <button onClick={() => setShowForm(v => !v)} data-testid="add-payroll-pledge-btn"
            className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
            style={{ background: primaryColor }}>
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? 'Close' : 'New payroll gift'}
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="px-6 py-5 border-b bg-gray-50 space-y-4" data-testid="payroll-pledge-form">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Cause</label>
              <select value={campaignId} onChange={e => setCampaignId(e.target.value)}
                data-testid="payroll-campaign-select"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white">
                <option value="">Choose a cause…</option>
                {campaigns.filter(c => c.charity_ein).map(c => (
                  <option key={c.campaign_id} value={c.campaign_id}>
                    {c.name || c.title} — {c.charity_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Amount per pay run
              </label>
              <div className="flex gap-2">
                {[10, 25, 50, 100].map(v => (
                  <button key={v} type="button" onClick={() => setAmount(v)}
                    data-testid={`payroll-amount-${v}`}
                    className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${
                      Number(amount) === v ? 'text-white border-transparent' : 'bg-white text-gray-600 border-gray-200'
                    }`}
                    style={Number(amount) === v ? { background: primaryColor } : {}}>
                    ${v}
                  </button>
                ))}
                <input type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)}
                  data-testid="payroll-amount-input"
                  className="w-24 px-3 py-2 border border-gray-200 rounded-xl text-sm" />
              </div>
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                <CalendarClock className="w-3.5 h-3.5" />
                ${Number(amount || 0).toLocaleString()} {freq} · ${(Number(amount || 0) * (data?.periods_per_year || 12)).toLocaleString()} a year
              </p>
            </div>
            <button type="submit" disabled={saving} data-testid="submit-payroll-pledge-btn"
              className="w-full py-3 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: primaryColor }}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Start giving from payroll
            </button>
          </form>
        )}

        <div className="divide-y">
          {(data?.pledges || []).map(p => (
            <div key={p.pledge_id} className="px-6 py-4 flex items-center gap-4" data-testid={`payroll-pledge-${p.pledge_id}`}>
              <div className="flex-1">
                <p className="font-semibold text-gray-900 text-sm">{p.charity_name}</p>
                <p className="text-xs text-gray-500">
                  {p.periods_deducted || 0} deduction{(p.periods_deducted || 0) !== 1 ? 's' : ''} ·
                  ${(p.total_given || 0).toLocaleString()} given
                </p>
              </div>
              <p className="font-bold text-gray-900">${p.amount_per_period?.toLocaleString()}<span className="text-xs font-normal text-gray-400">/pay</span></p>
              <button onClick={() => cancel(p.pledge_id)} data-testid={`cancel-payroll-pledge-${p.pledge_id}`}
                className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Stop this payroll gift">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(data?.pledges || []).length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-gray-500" data-testid="payroll-empty-state">
              No payroll gifts yet. Set one up and it comes out of your pay automatically.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayrollGiving;
